import {InfoIcon, PencilIcon} from '@primer/octicons-react'
import {AnchoredOverlay, IconButton, Label, Link, ProgressBar, Stack, Text} from '@primer/react'
import {useState} from 'react'

import {formatQuotaResetLabel} from '../hooks/use-quota-banner'
import {MANAGE_BUDGET_URL} from '../utils/copilot-chat-helpers'
import {UserLevelBudgetState} from '../utils/copilot-chat-types'
import {useEntitlement} from './quota/EntitlementContext'
import styles from './TokenUsageInfo.module.css'

interface TokenUsageInfoData {
  includedCredits: {
    percentUsed: number
    isExceeded: boolean
    resetLabel: string | null
    total: number | null
    used: number | null
  }
  additionalUsage: {
    enabled: boolean
    percentUsed: number
    isExceeded: boolean
    budgetAmount: number | null
    budgetSpend: number | null
  } | null
  monthlyLimit: {
    state: UserLevelBudgetState | null
    percentUsed: number
    subtitleLabel: string | null
  } | null
  isUserLevelBudget: boolean
}

const numberFormatter = new Intl.NumberFormat('en-US')
const currencyFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'})

function formatNumber(amount: number | null): string {
  if (amount === null) return '-'
  return numberFormatter.format(amount)
}

function formatCurrency(amount: number | null): string {
  if (amount === null) return '-'
  return currencyFormatter.format(amount)
}

function UsageHintPopover({title, description}: {title: string; description: string}) {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <AnchoredOverlay
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      renderAnchor={props => (
        <IconButton
          size="small"
          variant="invisible"
          icon={InfoIcon}
          {...props}
          aria-haspopup={undefined}
          aria-expanded={props['aria-expanded']}
          aria-label={`Learn more about ${title}`}
          aria-labelledby={undefined}
        />
      )}
      side="outside-top"
      align="center"
      width="medium"
    >
      <Stack direction="vertical" gap="normal" padding="spacious">
        <Text size="medium" weight="semibold">
          {title}
        </Text>
        <Text size="medium">{description}</Text>
      </Stack>
    </AnchoredOverlay>
  )
}

function useTokenUsageInfo(): TokenUsageInfoData | null {
  const {
    isIndividualPaidOrStudentPlan,
    overagesRemaining,
    overagesExceeded,
    overagesEnabled,
    overagesBudgetAmount,
    overagesBudgetSpend,
    quotaExceeded,
    quotaRemaining,
    quotaTotalAmount,
    quotaUsedAmount,
    quotaResetAt,
    licenseProvisioning,
    userLevelBudgetEnabled,
    userLevelBudgetState,
    userLevelBudgetPercentRemaining,
  } = useEntitlement()

  // Don't show while the license is still being provisioned to avoid misleading states.
  if (licenseProvisioning) return null

  // No quota data available yet.
  if (quotaRemaining === undefined) return null

  const percentUsed = Math.max(0, Math.min(100, 100 - quotaRemaining))
  const resetLabel = formatQuotaResetLabel(quotaResetAt)

  const showAdditionalUsage = isIndividualPaidOrStudentPlan
  const overagePercentUsed = overagesRemaining !== undefined ? Math.max(0, Math.min(100, 100 - overagesRemaining)) : 0

  const monthlyLimitPercentUsed =
    userLevelBudgetPercentRemaining !== null ? Math.max(0, Math.min(100, 100 - userLevelBudgetPercentRemaining)) : 0

  const monthlySubtitleLabel =
    userLevelBudgetState === UserLevelBudgetState.NoLimit
      ? 'You have no monthly limit on AI credits usage set by your organization.'
      : resetLabel
        ? `Reset ${resetLabel}`
        : null

  return {
    includedCredits: {
      percentUsed,
      isExceeded: quotaExceeded,
      resetLabel,
      total: quotaTotalAmount ?? null,
      used: quotaUsedAmount ?? null,
    },
    additionalUsage: showAdditionalUsage
      ? {
          enabled: overagesEnabled,
          percentUsed: overagePercentUsed,
          isExceeded: overagesExceeded,
          budgetAmount: overagesBudgetAmount ?? null,
          budgetSpend: overagesBudgetSpend ?? null,
        }
      : null,
    monthlyLimit: userLevelBudgetEnabled
      ? {state: userLevelBudgetState, percentUsed: monthlyLimitPercentUsed, subtitleLabel: monthlySubtitleLabel}
      : null,
    isUserLevelBudget: userLevelBudgetEnabled,
  }
}

export function TokenUsageInfo() {
  const data = useTokenUsageInfo()

  if (!data) return null

  const {includedCredits, additionalUsage, monthlyLimit, isUserLevelBudget} = data
  const includedCreditsProgressBarColor = includedCredits.percentUsed >= 75 ? 'severe.emphasis' : 'accent.emphasis'
  const additionalUsageProgressBarColor =
    (additionalUsage?.percentUsed ?? 0) >= 75 ? 'severe.emphasis' : 'accent.emphasis'

  return (
    <div>
      {!isUserLevelBudget && (
        <div className={styles.tokenUsageSection}>
          <div className={styles.tokenUsageSectionHeader}>
            <span className={styles.tokenUsageSectionTitle}>
              Included credits
              <UsageHintPopover
                title="Included credits"
                description="AI credits included with your plan, reset monthly. Enable additional usage to continue with pay-as-you-go credits once you run out of your included usage."
              />
            </span>
          </div>
          <div className={styles.tokenUsageDetails}>
            <span className={styles.tokenUsageDetailMuted}>
              {includedCredits.resetLabel ? `Reset ${includedCredits.resetLabel}` : ''}
            </span>
            {includedCredits.total != null && includedCredits.used != null ? (
              <span className={styles.tokenUsageDetail}>
                {formatNumber(includedCredits.used)}
                <span className={styles.tokenUsageDetailMuted}>
                  {' '}
                  / {formatNumber(includedCredits.total)} AI credits
                </span>
              </span>
            ) : null}
          </div>
          <ProgressBar barSize="small">
            <ProgressBar.Item
              progress={includedCredits.percentUsed}
              bg={includedCreditsProgressBarColor}
              aria-label={`Included credits: ${includedCredits.percentUsed}% used`}
              data-progress-bar-bg={includedCreditsProgressBarColor}
            />
          </ProgressBar>
        </div>
      )}

      {additionalUsage && (
        <>
          <div className={styles.tokenUsageDivider} />
          <div className={styles.tokenUsageSection}>
            <div className={styles.tokenUsageSectionHeader}>
              <span className={styles.tokenUsageSectionTitle}>
                Additional usage
                <UsageHintPopover
                  title="Additional usage"
                  description="Pay-as-you-go usage of additional AI credits once you run out of your included usage. Set a budget to cap your maximum monthly spend."
                />
              </span>
              <Link href={MANAGE_BUDGET_URL} className={styles.editBudgetLink}>
                <PencilIcon size={12} />
                Edit budget
              </Link>
            </div>
            <div className={styles.tokenUsageDetails}>
              <span className={styles.tokenUsageDetailMuted}>
                {additionalUsage.isExceeded ? 'Exceeded' : additionalUsage.enabled ? 'Enabled' : 'Disabled'}
              </span>
              {additionalUsage.budgetAmount != null && additionalUsage.budgetSpend != null ? (
                <span
                  className={styles.tokenUsageDetail}
                >{`${formatCurrency(additionalUsage.budgetSpend)} / ${formatCurrency(additionalUsage.budgetAmount)}`}</span>
              ) : null}
            </div>
            <ProgressBar
              progress={additionalUsage.percentUsed}
              bg={additionalUsageProgressBarColor}
              barSize="small"
              aria-label={`Additional usage: ${additionalUsage.percentUsed}% used`}
            />
          </div>
        </>
      )}

      {monthlyLimit && (
        <>
          {(!isUserLevelBudget || !!additionalUsage) && <div className={styles.tokenUsageDivider} />}
          <div className={styles.tokenUsageSection}>
            <div className={styles.tokenUsageSectionHeader}>
              <span className={styles.tokenUsageSectionTitle}>
                Monthly limit
                <UsageHintPopover
                  title="Monthly limit"
                  description="Usage limit per month, resets automatically. AI credit usage counts toward your monthly limit. Ask your account manager about increasing your overage when monthly limit is hit."
                />
              </span>
              {monthlyLimit.state === UserLevelBudgetState.NoLimit && <Label size="small">No usage limit</Label>}
              {monthlyLimit.state === UserLevelBudgetState.UsageBlocked && (
                <Label size="small" variant="danger">
                  Usage blocked
                </Label>
              )}
            </div>
            {monthlyLimit.subtitleLabel && (
              <div className={styles.tokenUsageDetails}>
                <span className={styles.tokenUsageDetailMuted}>{monthlyLimit.subtitleLabel}</span>
              </div>
            )}
            {monthlyLimit.state !== UserLevelBudgetState.NoLimit &&
              monthlyLimit.state !== UserLevelBudgetState.UsageBlocked && (
                <ProgressBar
                  progress={monthlyLimit.percentUsed}
                  bg={monthlyLimit.percentUsed >= 75 ? 'severe.emphasis' : 'accent.emphasis'}
                  barSize="small"
                  aria-label={`Monthly limit: ${monthlyLimit.percentUsed}% used`}
                />
              )}
          </div>
        </>
      )}
    </div>
  )
}
